import type { ReceivedOptionQuoteReply } from '@/api/option/type.ts';
import type { ReceivedFlexiForwardQuoteReply } from '@/store/flexi-forward/api/requestFlexiForwardRfs.model.ts';
import type { AccumulatorReplyMessage } from '@/store/forwardAccumulator/api/multiPass/requestForwardAccumulator.model.ts';
import type { ReceivedBidAskValues } from '@/store/types.ts';
import type { PredealCheckStatusHealth } from '@/types/common.ts';
import type { ISODate, Tenor } from '@/types/date.ts';

export type HeartbeatMessage = {
  Type: 'HEARTBEAT';
  Timeout: number;
  ReceivedFromIumTimestamp: number;
  Timestamp: number;
  RequestId: string;
  Heartbeat: {
    RfsId: string;
    Interval: number;
    RfsWindow: number;
  };
  OptionQuoteReply: null;
  AccumulatorQuoteReply: null;
  CashQuoteReply: null;
  BulkQuoteReply: null;
  SwapQuoteReply: null;
  QuoteReject: null;
  DealReject: null;
  Execute: null;
  BackOfficeReply: null;
};

export type ReceivedCashQuoteReply = {
  DistributionStream: string;
  RfsWindow: number;
  RfsId: string;
  // always null on DEVCI (confirmed 2026-09-17), don't rely on it
  QuoteType: 'QUOTE.REPLY' | 'QUOTE.WITHDRAW' | null;
  QuoteId: string;
  PredealChecks: ReceivedPredealCheck[];
  SpotWithoutMargin: ReceivedBidAskValues | null;
  SpotWithMargin: ReceivedBidAskValues | null;
  ForwardWithMargin: ReceivedBidAskValues | null;
  ForwardPointsWithoutMargin: ReceivedBidAskValues | null;
  ForwardPointsWithMargin: ReceivedBidAskValues | null;
  DefaultSpotMargin: ReceivedBidAskValues | null;
  DefaultForwardMarginPoints: ReceivedBidAskValues | null;
  MidAllInRate: string;
  ContraAmount: string;
  SgWay: string;
  CurrencyPair: string;
  TraderId: string;
};

export type ReceivedSwapQuoteReply = {
  QuoteId: string;
  QuoteType: 'Tradeable';
  RfsWindow: number;
  RfsId: string;
  DefaultMargin: ReceivedBidAskValues;
  DefaultSpotMargin: ReceivedBidAskValues;
  Spot: ReceivedBidAskValues;
  NearPoints: ReceivedBidAskValues;
  NearSalesPoints: ReceivedBidAskValues | null;
  FarPoints: ReceivedBidAskValues;
  FarMktPoints: ReceivedBidAskValues | null;
  NearSalesAllInRate: ReceivedBidAskValues | null;
  FarSalesAllInRate: ReceivedBidAskValues | null;
  NearDf2: number;
  FarDf2: number;
  MidAllInRate: number;
  Delta: number;
  TraderId: string | null;
  PredealChecks: ReceivedPredealCheck[];
  DistributionStream: string | null;
};

export type ReceivedPredealCheck = ReceivedPredealCheckIcon | ReceivedPredealCheckLabel;

export type ReceivedPredealCheckIcon = {
  Type: 'Icon';
  Label: string;
  StatusHealth: 'Ok' | 'Unknown';
};

export type ReceivedPredealCheckLabel = {
  Type: 'Label';
  Label: string;
  StatusHealth: 'Unknown';
  StatusValue: string;
};

export type ReceivedQuoteReject = {
  Type: 'QUOTE.ABORT';
  Timeout: number; // 0;
  ReceivedFromIumTimestamp: number; // 475018232841709;
  RequestId: string; // 'SGM_cuurino1vbj58jgkwclj5tnsp';
  Timestamp: number; // 1756909830664;
  OptionQuoteReply: null;
  AccumulatorQuoteReply: null;
  CashQuoteReply: null;
  BulkQuoteReply: null;
  SwapQuoteReply: null;
  Heartbeat: null;
  QuoteReject: QuoteReject;
  DealReject: null;
  Execute: null;
  BackOfficeReply: null;
};

export type ReceivedHeartbeat = {
  RfsWindow: number; // 4,
  Interval: number; // 4,
  RfsId: string; // "SGM_aw2jwriwdmkzxtbtv7tjv77h4"
};

export type QuoteReject = {
  ErrorCode: string; // 'EM_OVERSHOT_LIMIT';
  ErrorMessage: string; // 'The amount requested is too high. Please resubmit with a lower amount. user limit 80000 USD.';
  MultipassSessionId: string; //'SGM_cuurino1vbj58jgkwclj5tnsp';
  MultipassErrorCode: string; //'RrOvershotLimit';
  PredealChecks: ReceivedPredealCheck[];

  // not used in FX
  // RfsId: string; // 'SGM_cuurino1vbj58jgkwclj5tnsp';
  // CreditCheck: {
  //   Type: null;
  //   Limit: null;
  // };
};

export type ReceivedBulkQuoteReply = {
  DistributionStream: string; // "GOLD",
  RfsWindow: number; // 597,
  RfsId: string; // "SGM_aw2jwriwdmkzxtbtv7tjv77h4",
  QuoteType: null;
  QuoteId: string; // "to_be_accepted.4",
  PredealChecks: null;
  NotionalCurrency: string; // "EUR",
  NettedView: ReceivedBulkQuoteLeg[];
  Legs: ReceivedBulkQuoteLeg[];
  Timestamp: number;
};

export type ReceivedBulkQuoteLeg = {
  IsForward: boolean; // false;
  CustomerWay: 'Buy' | 'Sell';

  Amount: string; // '1000';

  Date: ISODate; // '2026-02-20';
  DateTenor: Tenor; // 'SP';

  SpotWithoutMargin: {
    Bid: string; // "0",
    Ask: string; // "2.37544"
  } | null;
  SpotWithMargin: {
    Bid: string; // "0",
    Ask: string; // "2.37557"
  };
  ForwardPoints: {
    Bid: string; // "0",
    Ask: string; // "0"
  };
  MidAllInRate: string; // "2.31422",
  DefaultMargin: {
    Bid: string; // '0';
    Ask: string; // '1.3';
  } | null;
  ForwardMarginPoints: {
    Bid: string; // '0';
    Ask: string; // '0';
  } | null;
};

type TraderNotifQuote = {
  TraderNotification: true;
  TraderId: string | null;
  QuoteContribution: QuoteContributionType | null;
  RfsWindow: number;
};

export type QuoteType =
  | ReceivedOptionQuoteReply
  | ReceivedFlexiForwardQuoteReply
  | ReceivedCashQuoteReply
  | ReceivedSwapQuoteReply
  | ReceivedBulkQuoteReply
  | ReceivedHeartbeat
  | ReceivedQuoteReject
  | AccumulatorReplyMessage;

export interface IQuoteTraderNotification<T extends QuoteType> {
  Type: 'QUOTE.REPLY';
  RequestId: string;
  OptionQuoteReply: T extends ReceivedOptionQuoteReply
    ? TraderNotifQuote
    : T extends ReceivedFlexiForwardQuoteReply
      ? TraderNotifQuote
      : null;
  AccumulatorQuoteReply: T extends AccumulatorReplyMessage ? TraderNotifQuote : null;
  Timestamp: number;
}

export interface IQuoteReply<T extends QuoteType> {
  Type: 'QUOTE.REPLY';
  RequestId: string;

  OptionQuoteReply: T extends ReceivedOptionQuoteReply
    ? ReceivedOptionQuoteReply
    : T extends ReceivedFlexiForwardQuoteReply
      ? ReceivedFlexiForwardQuoteReply
      : null;
  CashQuoteReply: T extends ReceivedCashQuoteReply ? ReceivedCashQuoteReply : null;
  SwapQuoteReply: T extends ReceivedSwapQuoteReply ? ReceivedSwapQuoteReply : null;
  BulkQuoteReply: T extends ReceivedBulkQuoteReply ? ReceivedBulkQuoteReply : null;
  Heartbeat: T extends ReceivedHeartbeat ? ReceivedHeartbeat : null;
  QuoteReject: T extends ReceivedQuoteReject ? ReceivedQuoteReject : null;
  AccumulatorQuoteReply: T extends AccumulatorReplyMessage ? AccumulatorReplyMessage : null;

  Timestamp: number;
  workerTimestamp: number;
}

export interface IQuoteWithdraw<T extends QuoteType> {
  Type: 'QUOTE.WITHDRAW';
  RequestId: string;

  OptionQuoteReply: T extends ReceivedOptionQuoteReply
    ? ReceivedOptionQuoteReply
    : T extends ReceivedFlexiForwardQuoteReply
      ? ReceivedFlexiForwardQuoteReply
      : null;
  CashQuoteReply: T extends ReceivedCashQuoteReply ? ReceivedCashQuoteReply : null;
  SwapQuoteReply: T extends ReceivedSwapQuoteReply ? ReceivedSwapQuoteReply : null;
  BulkQuoteReply: T extends ReceivedBulkQuoteReply ? ReceivedBulkQuoteReply : null;
  AccumulatorQuoteReply: T extends AccumulatorReplyMessage ? AccumulatorReplyMessage : null;

  Heartbeat: T extends ReceivedHeartbeat ? ReceivedHeartbeat : null;
  QuoteReject: T extends ReceivedQuoteReject ? ReceivedQuoteReject : null;
  Timestamp: number;
  workerTimestamp: number;
}

export interface IHeartbeat {
  Type: 'HEARTBEAT';
  RequestId: string;
  Heartbeat: {
    Interval: number;
    RfsWindow: number;
  };
}

export interface IQuoteAbort {
  Type: 'QUOTE.ABORT';
  RequestId: string;
  QuoteReject: {
    MultipassSessionId: string;
    MultipassErrorCode: string;
    ErrorCode: string;
    ErrorMessage: string;
    PredealChecks: readonly PredealCheck[];
  };
  workerTimestamp: number;
}

export type QuoteWithPrice<T extends QuoteType> = IQuoteReply<T> | IQuoteWithdraw<T>;
export type QuoteReply<T extends QuoteType> =
  | IQuoteTraderNotification<T>
  | QuoteWithPrice<T>
  | IHeartbeat
  | IQuoteAbort;

export function isQuoteWithPrice<T extends QuoteType>(event: QuoteReply<T>): event is QuoteWithPrice<T> {
  return isQuoteReply(event) || isQuoteWithdraw(event);
}

export function isQuoteWithdraw<T extends QuoteType>(event: QuoteReply<T>): event is IQuoteWithdraw<T> {
  return event.Type === 'QUOTE.WITHDRAW';
}

export function isQuoteTraderNotification<T extends QuoteType>(
  event: QuoteReply<T>,
): event is IQuoteTraderNotification<T> {
  if (event.Type !== 'QUOTE.REPLY') {
    return false;
  }

  if (event.OptionQuoteReply) {
    return event.OptionQuoteReply.TraderNotification;
  }

  if (event.AccumulatorQuoteReply) {
    return event.AccumulatorQuoteReply.TraderNotification;
  }

  return false;
}

export function isQuoteReply<T extends QuoteType>(event: QuoteReply<T>): event is IQuoteReply<T> {
  if (event.Type !== 'QUOTE.REPLY') {
    return false;
  }

  if (event.OptionQuoteReply) {
    return !event.OptionQuoteReply.TraderNotification;
  }

  if (event.AccumulatorQuoteReply) {
    return !event.AccumulatorQuoteReply.TraderNotification;
  }

  return true;
}

interface IPredealCheckStatusIcon {
  Type: 'Icon';
  Label: string;
  StatusHealth: PredealCheckStatusHealth;
}

interface IPredealCheckStatusLabel {
  Type: 'Label';
  StatusHealth: 'Unknown';
  Label: string;
  StatusValue: string | null;
}

export type PredealCheck = IPredealCheckStatusIcon | IPredealCheckStatusLabel;
export type QuoteContributionType = 'Automatic' | 'Manual';
